import {type StrNum} from "_types";


const _ = <P extends StrNum[]>(...args: P) => ['admin', ...args];

export const routes = {
    assignments: {
        search: _('assignments'),
        store: _('assignments', '?store'),
    },
    assignment: <R extends string>(id: R) => ({
        show: _('assignments', id),
        update: _('assignments', id),
        delete: _('assignments', id),
    }),

    attendances: {
        search: _('attendances'),
        mySearch: _('attendances', 'my'),
        store: _('attendances', '?store'),
        startSession: _('attendances', 'start-session'),
        endSession: _('attendances', 'end-session'),
        currentSession: _('attendances', 'current-session'),
    },
    attendance: <R extends string>(id: R) => ({
        show: _('attendances', id),
        end: _('attendances', id, 'end'),
        video: _('attendances', id, 'video'),
        chatToken: _('attendances', id, 'chat-token'),
        // update: _('attendances', id),
        // delete: _('attendances', id),
    }),

    auth: {
        currentUser: _('auth'),
        login: _('auth'),
        google: _('auth', 'google'),

        courts: _('auth', 'courts'),
        selectCourt: _('auth', 'courts?store'),
        uppdateCourt: _('auth', 'courts?patch'),

        logout: _('auth', 'logout'),
        resetPassword: _('auth', 'reset-password'),
        forgetPassword: _('auth', 'forget-password'),
    },

    dashboard: {
        stats: _('dashboard'),
    },

    chat: {
        messages: <R extends string>(id: R) => ({
            list: _('chat', id),
            participants: _('chat', id, 'participants'),
        }),
        allowClientServedToChat: _('chat', 'allow-client-served-to-chat'),
        denyClientServedToChat: _('chat', 'deny-client-served-to-chat'),
    },

    chatBot: {
        search: _('chat-bot'),
        get: <R extends string>(id: R) => _('chat-bot', id),
    },

    conferences: {
        search: _('conferences'),
        calendar: _('conferences', 'calendar'),
        store: _('conferences', '?store'),
    },
    conference: <R extends string>(id: R) => ({
        show: _('conferences', id),
        update: _('conferences', id),
        delete: _('conferences', id),
        chatToken: _('conferences', id, 'chat-token'),
        start: _('conferences', id, 'start'),
        stop: _('conferences', id, 'stop'),
        video: _('conferences', id, 'video'),
        cancel: _('conferences', id, 'cancel'),
        sendHistory: _('conferences', id, 'send-history'),
    }),

    conferenceDesks: {
        search: _('conference-desks'),
        store: _('conference-desks', '?store'),
    },
    conferenceDesk: <R extends string>(id: R) => ({
        show: _('conference-desks', id),
        update: _('conference-desks', id),
        delete: _('conference-desks', id),
    }),

    exceptionsDay: {
        search: _('exception-day'),
        store: _('exception-day', '?store'),
    },
    exceptionDay: <R extends string>(id: R) => ({
        show: _('exception-day', id),
        delete: _('exception-day', id),
    }),

    counties: {
        search: _('counties'),
        store: _('counties', '?store'),
    },
    county: <R extends string>(id: R) => ({
        show: _('counties', id),
        update: _('counties', id),
        delete: _('counties', id),
    }),

    courts: {
        search: _('courts'),
        store: _('courts', '?store'),
    },
    court: <R extends string>(id: R) => ({
        show: _('courts', id),
        update: _('courts', id),
        delete: _('courts', id),
        calendar: {
            hearings: _('courts', id, 'calendar', 'events', 'hearings'),
            conferences: _('courts', id, 'calendar', 'events', 'conferences'),
            event: (eventId: string) => _('courts', id, 'calendar', 'events', eventId),
            store: _('courts', id, 'calendar', '?store'),
        },
        loginAs: _('courts', id, 'login-as'),
    }),

    courtEmployees: {
        search: _('court-employees'),
        store: _('court-employees', '?store'),
    },
    courtEmployee: <R extends string>(id: R) => ({
        show: _('court-employees', id),
        update: _('court-employees', id),
        delete: _('court-employees', id),
    }),

    desks: {
        search: _('desks'),
        store: _('desks', '?store'),
    },
    desk: <R extends string>(id: R) => ({
        show: _('desks', id),
        update: _('desks', id),
        delete: _('desks', id),
    }),

    hearings: {
        search: _('hearings'),
        store: _('hearings', '?store'),

        employees: _('hearings', 'employees'),
        event: (id: string) => ({
            show: _('hearings', 'event', id),
        })
    },
    hearing: <R extends string>(id: R) => ({
        show: _('hearings', id),
        update: _('hearings', id),
        chatToken: _('hearings', id, 'chat-token'),
        start: _('hearings', id, 'start'),
        cancel: _('hearings', id, 'cancel'),
        stop: _('hearings', id, 'stop'),
        delete: _('hearings', id),
    }),

    judges: {
        search: _('judges'),
        store: _('judges', '?store'),
    },
    judge: <R extends string>(id: R) => ({
        show: _('judges', id),
        update: _('judges', id),
        delete: _('judges', id),
    }),

    myCalendar: {
        search: _('my-calendar'),
    },

    offices: {
        search: _('offices'),
        store: _('offices', '?store'),
    },
    office: <R extends string>(id: R) => ({
        show: _('offices', id),
        update: _('offices', id),
        delete: _('offices', id),
    }),

    profile: {
        photo: _('profile', 'photo?store'),
        update: _('profile'),
    },

    queues: {
        token: _('queues', 'token'),
    },

    regionals: {
        search: _('regionals'),
        store: _('regionals', '?store'),
    },
    regional: <R extends string>(id: R) => ({
        show: _('regionals', id),
        update: _('regionals', id),
        delete: _('regionals', id),
    }),


    logs: {
        search: _('logs'),
    },
    log: <R extends string>(id: R) => ({
        show: _('logs', id),
    }),

    reports: {
        search: _('reports'),
        permissions: _('reports', 'permissions'),
        store: _('reports', '?store'),
    },
    report: <R extends string>(id: R) => ({
        show: _('reports', id),
        update: _('reports', id),
        delete: _('reports', id),
    }),

    roles: {
        search: _('roles'),
        permissions: _('roles', 'permissions'),
        store: _('roles', '?store'),
    },
    role: <R extends string>(id: R) => ({
        show: _('roles', id),
        update: _('roles', id),
        delete: _('roles', id),
    }),

    twoFactor: {
        token: _('two-factor'),
        confirm: _('two-factor', '?confirm'),
        disable: _('two-factor'),
        validate: _('two-factor', 'validate'),
    },

    users: {
        search: _('users'),
        store: _('users', '?store'),
    },
    user: <R extends string>(id: R) => ({
        show: _('users', id),
        delete: _('users', id),
        update: _('users', id),
    }),
}
